import { addError } from "@wayflyer/blackbox-fe";

export const REOPEN_GROUP_PANEL_STORAGE_KEY = "funding-application:reopen-group-panel-intent";

const storageKeyFor = (customerDomain: string): string => `${REOPEN_GROUP_PANEL_STORAGE_KEY}:${customerDomain}`;
const TIME_TO_LIVE_MS = 15 * 60 * 1000;

interface ReopenGroupIntent {
  groupUuid: string;
  memberUuidsBeforeConnect: string[];
  timestamp: number;
}

interface ReopenGroupIntentResult {
  groupUuid: string;
  memberUuidsBeforeConnect: string[];
}

function isReopenGroupIntent(value: unknown): value is ReopenGroupIntent {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as ReopenGroupIntent;
  return (
    typeof candidate.groupUuid === "string" &&
    typeof candidate.timestamp === "number" &&
    Array.isArray(candidate.memberUuidsBeforeConnect) &&
    candidate.memberUuidsBeforeConnect.every((uuid) => typeof uuid === "string")
  );
}

export function setReopenGroupIntent(
  customerDomain: string,
  groupUuid: string,
  memberUuidsBeforeConnect: string[]
): void {
  try {
    const intent: ReopenGroupIntent = { groupUuid, memberUuidsBeforeConnect, timestamp: Date.now() };
    localStorage.setItem(storageKeyFor(customerDomain), JSON.stringify(intent));
  } catch (cause) {
    // Without this, the group panel just won't reopen after the redirect - degraded UX worth a
    // signal, not worth failing the connect attempt itself over.
    addError(cause instanceof Error ? cause : new Error(String(cause)), { failedAt: "reopen-group-intent-save" });
  }
}

// Single-use: always clears the stored intent, whether or not it was still valid, so a later
// unrelated page load can't be mistaken for a return trip.
export function consumeReopenGroupIntent(customerDomain: string): ReopenGroupIntentResult | null {
  try {
    const key = storageKeyFor(customerDomain);
    const raw = localStorage.getItem(key);
    localStorage.removeItem(key);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!isReopenGroupIntent(parsed) || Date.now() - parsed.timestamp > TIME_TO_LIVE_MS) {
      return null;
    }

    return { groupUuid: parsed.groupUuid, memberUuidsBeforeConnect: parsed.memberUuidsBeforeConnect };
  } catch {
    return null;
  }
}
